import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import {
  AchievementDialog,
} from './AchievementDialog'

import type {
  AchievementUnlocked,
} from '../../types/achievements'


interface AchievementContextValue {
  showAchievement: (
    achievement: AchievementUnlocked,
  ) => void

  showAchievements: (
    achievements:
      | AchievementUnlocked[]
      | null
      | undefined,
  ) => void
}


interface AchievementProviderProps {
  children: React.ReactNode
}


const AchievementContext =
  createContext<AchievementContextValue | null>(
    null,
  )


export function AchievementProvider({
  children,
}: AchievementProviderProps) {
  const [
    queue,
    setQueue,
  ] = useState<AchievementUnlocked[]>([])


  const showAchievement =
    useCallback(
      (
        achievement: AchievementUnlocked,
      ) => {
        setQueue(
          (current) => [
            ...current,
            achievement,
          ],
        )
      },
      [],
    )


  const showAchievements =
    useCallback(
      (
        achievements:
          | AchievementUnlocked[]
          | null
          | undefined,
      ) => {
        if (
          !achievements ||
          achievements.length === 0
        ) {
          return
        }

        setQueue(
          (current) => [
            ...current,
            ...achievements,
          ],
        )
      },
      [],
    )


  const handleClose =
    useCallback(() => {
      setQueue(
        (current) =>
          current.slice(1),
      )
    }, [])


  const value =
    useMemo<AchievementContextValue>(
      () => ({
        showAchievement,
        showAchievements,
      }),
      [
        showAchievement,
        showAchievements,
      ],
    )


  const currentAchievement =
    queue[0] ?? null


  return (
    <AchievementContext.Provider
      value={value}
    >
      {children}

      <AchievementDialog
        achievement={currentAchievement}
        remainingCount={
          Math.max(
            queue.length - 1,
            0,
          )
        }
        onClose={handleClose}
      />
    </AchievementContext.Provider>
  )
}


export function useAchievements():
  AchievementContextValue {
  const context =
    useContext(
      AchievementContext,
    )

  if (!context) {
    throw new Error(
      'useAchievements must be used '
      + 'inside AchievementProvider',
    )
  }

  return context
}
